// The per-cell type chip + picker popover, shared by AUDIO and VISUAL cells.
//
// Rendered inside the ScriptCell NodeView (see ScriptCell.ts). Each kind passes
// its own config (taxonomy, icons, copy, data attribute); the DOM, styling and
// behaviour are the same. Plain DOM - same approach as ScriptRow's delete
// button and RowMenu - so it works identically in the editable admin editor
// and the read-only client review editor.
//
//   editable + typed    → a button chip (icon + label + caret) that opens the
//                         picker to change the type.
//   editable + untyped  → a subtle "ghost" chip that opens the picker to assign
//                         a type (existing rows have no type yet).
//   read-only + typed   → a static label chip (no caret, not interactive).
//   read-only + untyped → nothing (el is null); the cell keeps its plain "A" /
//                         "V" marker, unchanged from before the feature existed.
//
// Colour is never the only signal - every chip carries an icon and a word, so
// it survives colour-blindness, greyscale and the PDF export.

import { AUDIO_TYPE_META, AUDIO_TYPES, type AudioType } from '../../../shared/audioTypes';
import { VISUAL_TYPE_META, VISUAL_TYPES, type VisualType } from '../../../shared/visualTypes';

// ---- Lucide icons (1.5–1.6px stroke, per the brand's icon rule) ----
// Exported so the Script Shoot View draws the same chip outside the editor.
export const AUDIO_TYPE_ICONS: Record<AudioType, string> = {
  voiceover:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M2 10v3"/><path d="M6 6v11"/><path d="M10 3v18"/><path d="M14 8v7"/><path d="M18 5v13"/><path d="M22 10v3"/></svg>',
  to_camera:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m16 13 5.2 3.5a.5.5 0 0 0 .8-.4V7.9a.5.5 0 0 0-.8-.4L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>',
  interview:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>',
  silent:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" x2="15" y1="20" y2="20"/><line x1="12" x2="12" y1="4" y2="20"/></svg>',
};

export const VISUAL_TYPE_ICONS: Record<VisualType, string> = {
  talent:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>',
  broll:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 3v18"/><path d="M17 3v18"/><path d="M3 8h4"/><path d="M3 16h4"/><path d="M17 8h4"/><path d="M17 16h4"/></svg>',
  text:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M6 11h4"/><path d="M6 15h8"/><path d="M14 11h4"/></svg>',
  graphics:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8.3 10a.7.7 0 0 1-.6-1.1L11.4 3a.7.7 0 0 1 1.2 0l3.7 5.9a.7.7 0 0 1-.6 1.1z"/><rect x="3" y="14" width="7" height="7" rx="1"/><circle cx="17.5" cy="17.5" r="3.5"/></svg>',
  screen:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>',
};

const CARET =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
const CHECK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const GHOST_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>';

/** Everything that differs between the audio chip and the visual chip. */
export interface CellTypeConfig<T extends string> {
  kind: 'audio' | 'visuals';
  /** Picker heading. */
  heading: string;
  /** Ghost chip label, plus its tooltip and accessible name. */
  ghostLabel: string;
  ghostTitle: string;
  ghostAria: string;
  /** Prefix for a typed chip's accessible name ("Audio type: Voiceover. Change"). */
  ariaNoun: string;
  /** Picker order. */
  order: readonly T[];
  meta: Record<T, { label: string; gloss: string }>;
  icons: Record<T, string>;
  /** Attribute the chip carries its type on (the CSS palette keys off it). */
  dataAttr: 'data-audio-type' | 'data-visual-type';
}

export const AUDIO_CHIP_CONFIG: CellTypeConfig<AudioType> = {
  kind: 'audio',
  heading: 'Audio type',
  ghostLabel: 'Audio type',
  ghostTitle: 'Set the audio type for this line',
  ghostAria: 'Set audio type',
  ariaNoun: 'Audio type',
  order: AUDIO_TYPES,
  meta: AUDIO_TYPE_META,
  icons: AUDIO_TYPE_ICONS,
  dataAttr: 'data-audio-type',
};

export const VISUAL_CHIP_CONFIG: CellTypeConfig<VisualType> = {
  kind: 'visuals',
  heading: 'Visual type',
  ghostLabel: 'Visual type',
  ghostTitle: 'Set the visual type for this line',
  ghostAria: 'Set visual type',
  ariaNoun: 'Visual type',
  order: VISUAL_TYPES,
  meta: VISUAL_TYPE_META,
  icons: VISUAL_TYPE_ICONS,
  dataAttr: 'data-visual-type',
};

export interface CellTypeChipHandle<T extends string> {
  /** The chip element, or null when nothing should render (read-only untyped). */
  el: HTMLElement | null;
  /** Re-render for a new type value (the NodeView calls this on attr change). */
  update: (type: T | null) => void;
  destroy: () => void;
}

interface MountOptions<T extends string> {
  editable: boolean;
  getType: () => T | null;
  onSelect: (type: T) => void;
}

export function mountCellTypeChip<T extends string>(
  config: CellTypeConfig<T>,
  opts: MountOptions<T>,
): CellTypeChipHandle<T> {
  const { editable, onSelect } = opts;
  let type = opts.getType();

  // Read-only + untyped → render nothing at all.
  if (!editable && type == null) {
    return { el: null, update: () => {}, destroy: () => {} };
  }

  const el = document.createElement(editable ? 'button' : 'span');
  el.className = 'cell-type-chip';
  // The chip is non-content chrome sitting inside the ProseMirror editable
  // region; mark it so the caret, IME, spellcheck and select-all can't enter
  // or mutate it (matching ScriptRow's delete button and BlockCommentTargets).
  el.setAttribute('contenteditable', 'false');
  if (editable) {
    (el as HTMLButtonElement).type = 'button';
    el.setAttribute('aria-haspopup', 'menu');
    // Don't let the click move/lose the editor selection.
    el.addEventListener('mousedown', (e) => e.preventDefault());
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openPicker(config, el, opts.getType(), onSelect);
    });
  } else {
    el.classList.add('cell-type-chip--readonly');
  }

  render();

  function render() {
    if (type == null) {
      // Ghost affordance (editable only - read-only untyped returned early).
      el.classList.add('cell-type-chip--ghost');
      el.removeAttribute(config.dataAttr);
      el.title = config.ghostTitle;
      el.setAttribute('aria-label', config.ghostAria);
      el.innerHTML =
        `<span class="cell-type-chip__ic">${GHOST_ICON}</span>` +
        `<span class="cell-type-chip__label">${config.ghostLabel}</span>` +
        (editable ? `<span class="cell-type-chip__caret">${CARET}</span>` : '');
      return;
    }
    const meta = config.meta[type];
    el.classList.remove('cell-type-chip--ghost');
    el.setAttribute(config.dataAttr, type);
    el.title = `${meta.label}. ${meta.gloss}`;
    if (editable) el.setAttribute('aria-label', `${config.ariaNoun}: ${meta.label}. Change`);
    el.innerHTML =
      `<span class="cell-type-chip__ic">${config.icons[type]}</span>` +
      `<span class="cell-type-chip__label">${meta.label}</span>` +
      (editable ? `<span class="cell-type-chip__caret">${CARET}</span>` : '');
  }

  return {
    el,
    update(next) {
      if (next === type) return;
      type = next;
      render();
    },
    destroy() {
      if (activeChip === el) closePicker();
    },
  };
}

// ---------------------------------------------------------------------------
// Picker popover - a single shared instance for both kinds, opened under
// whichever chip was clicked (so opening a visual picker closes an open audio
// one and the reverse). Portalled to <body> (position: fixed) so it escapes
// the editor's overflow and stacking contexts.
// ---------------------------------------------------------------------------

let activeChip: HTMLElement | null = null;
let activeMenu: HTMLElement | null = null;
let detach: (() => void) | null = null;

const MENU_WIDTH = 248;
const GAP = 6;

function openPicker<T extends string>(
  config: CellTypeConfig<T>,
  chip: HTMLElement,
  current: T | null,
  onSelect: (type: T) => void,
) {
  if (activeChip === chip) {
    closePicker();
    return;
  }
  closePicker();

  const menu = document.createElement('div');
  menu.className = 'cell-type-menu';
  menu.setAttribute('role', 'menu');

  const head = document.createElement('div');
  head.className = 'cell-type-menu__head';
  head.textContent = config.heading;
  menu.appendChild(head);

  for (const t of config.order) {
    const meta = config.meta[t];
    const opt = document.createElement('button');
    opt.type = 'button';
    opt.className = 'cell-type-opt';
    opt.setAttribute('role', 'menuitemradio');
    opt.setAttribute('aria-checked', String(t === current));
    if (t === current) opt.classList.add('is-current');
    opt.innerHTML =
      `<span class="cell-type-opt__ic cell-type-opt__ic--${t}">${config.icons[t]}</span>` +
      `<span class="cell-type-opt__body">` +
      `<span class="cell-type-opt__name">${meta.label}` +
      `<span class="cell-type-opt__check">${CHECK}</span></span>` +
      `<span class="cell-type-opt__gloss">${meta.gloss}</span></span>`;
    opt.addEventListener('mousedown', (e) => e.preventDefault());
    opt.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(t);
      closePicker();
      (chip as HTMLElement).focus?.();
    });
    menu.appendChild(opt);
  }

  document.body.appendChild(menu);
  activeChip = chip;
  activeMenu = menu;
  chip.setAttribute('data-open', 'true');

  position();

  // Focus the current option (or the first) for keyboard users.
  const opts = Array.from(menu.querySelectorAll<HTMLButtonElement>('.cell-type-opt'));
  const startIdx = Math.max(0, current == null ? -1 : config.order.indexOf(current));
  opts[startIdx]?.focus();

  function position() {
    const r = chip.getBoundingClientRect();
    let left = r.left;
    left = Math.min(left, window.innerWidth - MENU_WIDTH - 8);
    left = Math.max(8, left);
    const maxH = window.innerHeight - 16;
    // Width and max-height first: the glosses wrap at MENU_WIDTH, so the
    // height is only right once the width is applied.
    menu.style.width = `${MENU_WIDTH}px`;
    menu.style.maxHeight = `${maxH}px`;
    const below = r.bottom + GAP;
    const menuH = Math.min(menu.offsetHeight || 260, maxH);
    const above = r.top - GAP - menuH;
    const top =
      below + menuH <= window.innerHeight - 8
        ? below
        : above >= 8
          ? above
          : Math.max(8, window.innerHeight - 8 - menuH);
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
  }

  function onDocPointer(e: Event) {
    const target = e.target as Node;
    if (menu.contains(target) || chip.contains(target)) return;
    closePicker();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePicker();
      (chip as HTMLElement).focus?.();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const list = Array.from(menu.querySelectorAll<HTMLButtonElement>('.cell-type-opt'));
      const idx = list.findIndex((b) => b === document.activeElement);
      const nextIdx =
        e.key === 'ArrowDown'
          ? (idx + 1) % list.length
          : (idx - 1 + list.length) % list.length;
      list[nextIdx]?.focus();
    }
  }

  document.addEventListener('mousedown', onDocPointer, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', position, true);
  window.addEventListener('resize', position);

  detach = () => {
    document.removeEventListener('mousedown', onDocPointer, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('scroll', position, true);
    window.removeEventListener('resize', position);
  };
}

function closePicker() {
  detach?.();
  detach = null;
  if (activeChip) activeChip.removeAttribute('data-open');
  activeMenu?.remove();
  activeChip = null;
  activeMenu = null;
}
